import { matchIsValidTel } from 'mui-tel-input';
import { useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { getDiffBetweenNestedObj } from '../utils/helpers';

export function useFormRules() {
  const { t } = useTranslation('inputs');
  // eslint-disable-next-line max-len
  const emailReg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const isEmptyInput = (v: string) => {
    if (v === undefined) return false;
    return v === null || !v.length;
  };

  const emailValidation = (v: string) => isEmptyInput(v) || emailReg.test(v) || t('emailIsNotValid');

  const requiredField = (value: any) => (
    !!value
      || value === 0
      || (value && value?.trim() !== '')
      || 'Required Field'
  );

  const minCharacters = (numberOfCharacters: number | string) => (v: string) => (
    isEmptyInput(v)
        || (v && v.length) >= numberOfCharacters
        || `${numberOfCharacters} characters minimum`
  );

  const oneLowerCase = (v: string) => isEmptyInput(v) || /[a-z]/.test(v) || 'One lowercase character';

  const oneUpperCase = (v: string) => isEmptyInput(v) || /[A-Z]/.test(v) || 'One uppercase character';

  const isExists = (v: string, list: Array<string>) => {
    const formattedList = (list || [])?.map((item) => item?.toLowerCase());
    return formattedList.includes(v?.toLowerCase());
  };

  const isEmailExists = <T extends { email: string }>(
    v: string,
    list: Array<T>,
  ) => {
    if (!isEmptyInput(v)) {
      const isExists = list.find((item) => item.email?.toLowerCase() === v?.toLowerCase());

      if (isExists) {
        return 'The user already exists';
      }
      return true;
    }
    return true;
  };

  const isPhoneValid = (val: string) => {
    if (isEmptyInput(val)) return true;
    return matchIsValidTel(val);
  };

  const taskValidation = <T extends { taskName: string }>(
    v: string,
    list: Array<T>,
  ) => {
    const isTaskExists = list.find((task) => task.taskName === v);

    if (isTaskExists) return t('taskNameAlreadyExists');
    return true;
  };

  const isEmailUnique = <T extends { email: string }>(editedEmail: string, rows: Array<T>) => {
    if (isEmptyInput(editedEmail)) return true;

    const existingEmails = rows.map((row) => row.email);
    return !existingEmails.includes(editedEmail);
  };

  return {
    minCharacters,
    oneLowerCase,
    oneUpperCase,
    isExists,
    isEmailExists,
    taskValidation,
    emailValidation,
    requiredField,
    isPhoneValid,
    isEmptyInput,
    isEmailUnique,
    emailReg,
    // userExists,
  };
}

export function useCustomForm<T>(initFormState: T) {
  const [formInputs, setFormInputs] = useState(initFormState);
  const [prevFormInputs, setPrevFormInputs] = useState(initFormState);
  const handleFormChange = (key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState, [key]: value,
    }));
  };

  const isEdited = useMemo(() => {
    const diff = getDiffBetweenNestedObj(
      formInputs as { [key: string]: typeof initFormState },
      prevFormInputs as { [key: string]: typeof initFormState },
    );

    return !isEmpty(diff);
  }, [formInputs, prevFormInputs]);

  return {
    formInputs,
    setFormInputs,
    handleFormChange,
    isEdited,
    setPrevFormInputs,
  };
}
